import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getBestAuthToken } from '../../utils/authHeaders';
import LoadingSpinner from '../ui/LoadingSpinner';

/**
 * Decode JWT payload to extract role. Used as fallback when React state
 * hasn't committed yet (e.g. right after login).
 */
function decodeRoleFromToken(): string | null {
  try {
    const token = getBestAuthToken();
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1])).role ?? null;
  } catch {
    return null;
  }
}

/**
 * Layout guard: requires a valid session before rendering nested routes.
 * Sends anonymous users to /login with return location.
 *
 * For partner users hitting /, redirects to /partner/dashboard immediately.
 */
export const RequireAuth: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const hasSessionToken = Boolean(getBestAuthToken());

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#e4ebe6]"
        role="status"
        aria-live="polite"
        aria-label="Checking authentication"
      >
        <div className="flex flex-col items-center gap-4">
          <LoadingSpinner />
          <span className="text-sm text-gray-600">Loading workspace...</span>
        </div>
      </div>
    );
  }

  if (!user || !hasSessionToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Partner users hitting root (/) should go to /partner/dashboard
  // Read from context first, fall back to JWT decode for login race condition
  const role = user.role ?? decodeRoleFromToken();
  if (role === 'partner_user' && location.pathname === '/') {
    return <Navigate to="/partner/dashboard" replace />;
  }

  return <Outlet />;
};
